/**
 * 퀘스트 영상 재생 워치독 — 어떤 이유로 멈춰도 **반드시** 다음 단계로 빠져나가게 한다.
 *
 * 원본: `QuestTagPage.FirstMissionIntro`(2026-08-03 첫 미션 인트로). OX 수행 흐름 3화면이
 * 같은 재생 계약을 쓰게 되면서(2026-08-12) **훅으로 추출**했다 — 120줄을 세 군데 복제하면
 * 아래 함정 주석이 갈라지고, 한 곳만 고쳐진 채 나머지가 잠긴다.
 *
 * 워치독 2종
 *   · 로딩 워치독   : 마운트 → `onPlaying`.  재생이 시작조차 못 하고 `onError` 도 안 오는 상태.
 *   · 무진행 워치독 : `onPlaying` → `onEnded`. 재생 중 디코더·네트워크 정지.
 *
 * 🔴 무진행 감지를 `timeupdate`/`progress` **이벤트 핸들러로 만들면 안 된다** — 정지하면
 *    이벤트 발화가 끊겨 판정 코드가 영원히 실행되지 않는다. 그래서 `setInterval` 폴링으로
 *    `video.currentTime` 스냅샷을 비교한다. 정상 재생은 `currentTime` 이 계속 늘어 절대
 *    잘리지 않는다(설계 D4 준수).
 *
 * 회귀 가드: QuestTagPage.test.js(워치독 15건) · OxQuizFlowScreens.test.jsx
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export const INTRO_VIDEO_LOAD_TIMEOUT_MS = 10000; // 로딩 워치독 만료(마운트 → onPlaying)
export const INTRO_VIDEO_STALL_TICK_MS = 1000; // 무진행 폴링 주기
export const INTRO_VIDEO_STALL_LIMIT_TICKS = 5; // currentTime 5초 연속 무증가 → 이탈

// 이탈 사유 — 서버 `INTRO_VIDEO_ERROR_REASONS`(`api-server/src/routers/tools_quest.py`)
// 와 **문자열이 정확히 일치**해야 한다. 어긋나면 알림이 "미등록 사유"로 접히고 throttle 키도
// `other` 하나로 뭉쳐져 원인 구분이 사라진다.
export const INTRO_VIDEO_REASON_LOAD_TIMEOUT = 'load_timeout';
export const INTRO_VIDEO_REASON_STALL = 'stall';
export const INTRO_VIDEO_REASON_ERROR = 'error';

/**
 * @param {object} params
 * @param {boolean} params.active   - 지금이 영상 재생 구간인가(URL 유무와 별개)
 * @param {string|null} params.videoUrl
 * @param {() => void} params.onFinished - 정상 종료·이탈 **모두** 여기로 나간다(정확히 1회)
 * @param {(reason: string) => void} [params.onAbort] - 이탈 사유 보고(fire-and-forget)
 * @param {string} [params.logLabel] - 버퍼링 경고 로그에 붙일 이름
 * @returns {{videoRef, videoPlaying: boolean, isVideoPhase: boolean, videoHandlers: object}}
 */
export default function useQuestVideoWatchdog({
  active, videoUrl, onFinished, onAbort, logLabel = '퀘스트 영상',
}) {
  // Android WebView가 autoplay를 실제로 시작하기 전 네이티브 플레이 아이콘을 잠깐 그린다.
  // 영상은 계속 미리 로드·자동 재생하되 playing 이벤트 전까지만 가림막 뒤에 숨긴다.
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const loadTimerRef = useRef(null);
  const stallTimerRef = useRef(null);
  const lastTimeRef = useRef(-1);
  const stallTicksRef = useRef(0);
  const settledRef = useRef(false);

  // 🔴 워치독 타이머는 **발화 시점의** 콜백을 불러야 한다.
  //    · 렌더 시점 클로저를 굳히면 부모가 리렌더된 뒤 옛 핸들러가 호출된다.
  //    · 반대로 콜백을 effect 의존성에 넣으면 부모 리렌더마다 워치독이 리셋돼
  //      **정지 상태가 영원히 감지되지 않는다**(잠김 재발).
  //    두 함정을 동시에 피하는 방법이 최신값 ref 다.
  const handlersRef = useRef({ onFinished, onAbort });
  useEffect(() => {
    handlersRef.current = { onFinished, onAbort };
  });

  const clearWatchdogs = useCallback(() => {
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
    if (stallTimerRef.current) {
      clearInterval(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  /**
   * 영상 구간의 **단일 출구** — 정상 종료(`onEnded`, reason=null)와 이탈(`onError`·워치독,
   * reason=사유)이 같은 경로로 나간다. 워치독과 `onEnded` 가 거의 동시에 와도 다음 단계로는
   * **정확히 1회**만 넘긴다.
   */
  const settleVideo = useCallback((reason) => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearWatchdogs();
    const handlers = handlersRef.current || {};
    if (reason && handlers.onAbort) {
      // 보고는 fire-and-forget 이다. 여기서 예외가 새면 아래 onFinished 가 실행되지 않아
      // **이탈을 감지하고도 화면이 잠긴다** — 워치독을 만든 목적 자체가 무너진다.
      try {
        handlers.onAbort(reason);
      } catch (err) {
        console.warn(`[useQuestVideoWatchdog] ${logLabel} 이탈 보고 호출 실패`, err);
      }
    }
    if (handlers.onFinished) handlers.onFinished();
  }, [clearWatchdogs, logLabel]);

  const isVideoPhase = !!active && !!videoUrl;

  // ── 로딩 워치독 — 마운트 → onPlaying 구간 ──────────────────────────────────
  // 무진행 폴링은 onPlaying 이후만 감시하므로, "재생이 시작조차 못 하고 onError 도 안 오는"
  // 상태는 이 타이머만 잡는다. 기획 §6 R-2 가 말한 "앱 재시작 외 탈출 불가"가 바로 그 상태다.
  useEffect(() => {
    if (!isVideoPhase) return undefined;
    settledRef.current = false;
    lastTimeRef.current = -1;
    stallTicksRef.current = 0;
    setVideoPlaying(false);
    loadTimerRef.current = setTimeout(() => {
      loadTimerRef.current = null;
      settleVideo(INTRO_VIDEO_REASON_LOAD_TIMEOUT);
    }, INTRO_VIDEO_LOAD_TIMEOUT_MS);
    return clearWatchdogs; // 언마운트·URL 교체 시 두 워치독 모두 해제
  }, [isVideoPhase, videoUrl, settleVideo, clearWatchdogs]);

  // 🔴 방어 — 재생 구간인데 URL 이 없으면 렌더할 것이 없어 가림막만 남고 잠긴다.
  //    부모가 URL 부재 시 이 구간을 건너뛰도록 라우팅하지만, 여기서도 즉시 다음 단계로
  //    넘겨 잠김 경로를 봉쇄한다.
  useEffect(() => {
    if (!active || videoUrl) return;
    const handlers = handlersRef.current || {};
    if (handlers.onFinished) handlers.onFinished();
  }, [active, videoUrl]);

  // ── 무진행 폴링 워치독 — onPlaying → onEnded 구간 ──────────────────────────
  const handleVideoPlaying = useCallback(() => {
    // 이미 이탈 처리된 뒤 뒤늦게 재생이 시작되는 경우가 있다(로딩 워치독 만료 직후 버퍼가 참).
    // 그대로 두면 아무도 소비하지 않는 폴링 인터벌이 새로 뜬다.
    if (settledRef.current) return;
    setVideoPlaying(true);
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
    if (stallTimerRef.current) return; // 버퍼링 후 재개로 onPlaying 재발화 — 인터벌은 1개만
    lastTimeRef.current = -1;
    stallTicksRef.current = 0;
    stallTimerRef.current = setInterval(() => {
      const el = videoRef.current;
      const current = el && Number.isFinite(el.currentTime) ? el.currentTime : lastTimeRef.current;
      if (current > lastTimeRef.current) {
        // 진행 중 — 정상 재생은 currentTime 이 계속 늘어 **절대 잘리지 않는다**(D4 준수).
        lastTimeRef.current = current;
        stallTicksRef.current = 0;
        return;
      }
      stallTicksRef.current += 1;
      if (stallTicksRef.current >= INTRO_VIDEO_STALL_LIMIT_TICKS) {
        settleVideo(INTRO_VIDEO_REASON_STALL);
      }
    }, INTRO_VIDEO_STALL_TICK_MS);
  }, [settleVideo]);

  const handleVideoEnded = useCallback(() => settleVideo(null), [settleVideo]);
  const handleVideoError = useCallback(
    () => settleVideo(INTRO_VIDEO_REASON_ERROR), [settleVideo],
  );

  /* `waiting`/`stalled` 는 **로그만** 남긴다 — 판정은 폴링 워치독 단독이다.
     이 이벤트들은 정지가 길어지면 더 오지 않으므로 판정에 쓰면 감지가 사라진다. */
  const videoHandlers = {
    onPlaying: handleVideoPlaying,
    onEnded: handleVideoEnded,
    onError: handleVideoError,
    onWaiting: () => console.warn(`[useQuestVideoWatchdog] ${logLabel} 버퍼링(waiting)`),
    onStalled: () => console.warn(`[useQuestVideoWatchdog] ${logLabel} 데이터 정체(stalled)`),
  };

  return { videoRef, videoPlaying, isVideoPhase, videoHandlers };
}
